/**
 * Client for the public delivery-tracking endpoint (delivery-program task).
 *
 *   GET /v1/delivery-mgmt/public/track/{token}  (no user/tenant auth)
 *
 * Deliberately a standalone fetch helper rather than a `services.ts` /
 * `http.ts` addition: another session owns `src/api/services.ts` tonight
 * (see .coordination/LOCKS.md), and this endpoint's shape (no tenant slug in
 * its URL, no Authorization header) doesn't fit `apiFetch`'s
 * tenant-API-key-gated flow anyway — see the note on `resolveTrackingApiBase`
 * below.
 *
 * 404 means "unknown or expired token" and is surfaced as `null` rather than
 * a thrown ApiError, since the caller (PublicDeliveryTrackingScreen) treats
 * it as a first-class UI state ("this link has expired"), not a failure to
 * retry.
 */

// Mirrors the host split in `src/api/http.ts` (STAGING_BASE / PROD_BASE):
// order-staging.tappya.com and localhost hit the staging gateway, everything
// else hits prod. Duplicated here (rather than imported) because
// `src/api/http.ts` is not on this task's file list and importing from it
// would still leave the two lists able to drift — small enough to keep in
// sync by inspection.
const STAGING_BASE = "https://api-staging.tappya.com";
const PROD_BASE = "https://api.tappya.com";
const STAGING_HOSTS = new Set(["order-staging.tappya.com", "localhost", "127.0.0.1"]);

function resolveTrackingApiBase(): string {
  const envBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? undefined;
  if (envBase) return envBase;
  if (typeof window !== "undefined" && STAGING_HOSTS.has(window.location.hostname)) {
    return STAGING_BASE;
  }
  return PROD_BASE;
}

/**
 * Gateway API key for this UNAUTHENTICATED, tenant-less page. The tenant
 * `X-API-KEY` used everywhere else in this app (`ensureStorefrontApiKey` in
 * `storefrontKey.ts`) is fetched from `/v1/online-ordering/config/{slug}` —
 * but the tracking link `https://order.tappya.com/track/{token}` carries no
 * tenant slug by design (a driver's location must not require the customer
 * to know which tenant they ordered from, and the token alone identifies
 * the delivery). This page therefore needs a single, tenant-agnostic public
 * key for the gateway's `/v1/delivery-mgmt/public/*` route, configured via
 * `VITE_PUBLIC_TRACKING_API_KEY`.
 *
 * ⚠️ Open item for the backend/gateway owner (flagged in the task report):
 * no such tenant-agnostic key exists yet as far as this session could find
 * — `config.json`/`.env.example` only carry per-tenant keys. Until one is
 * provisioned and wired into the gateway's `public/track` route allowlist,
 * this reads as `undefined` and the request goes out without `X-API-Key`,
 * which the gateway will reject with 403. That failure renders as this
 * screen's ordinary "couldn't load" retry state, not a crash.
 */
function trackingApiKey(): string | undefined {
  return import.meta.env.VITE_PUBLIC_TRACKING_API_KEY as string | undefined;
}

export interface TrackingStatusLabel {
  en: string;
  ar: string;
}

export interface TrackingDriverLocation {
  lat: number;
  lng: number;
}

export interface PublicTrackingResponse {
  /** DMS enum value, e.g. IN_TRANSIT, AT_CUSTOMER; compare via deliveryTrackingStatus helpers. */
  status: string;
  statusLabel: TrackingStatusLabel;
  branchName: string | null;
  driverFirstName: string | null;
  vehicleType: string | null;
  driverLocation: TrackingDriverLocation | null;
  etaMinutes: [number, number] | null;
  updatedAt: string;
}

export class TrackingNotFoundError extends Error {
  constructor() {
    super("Tracking link not found or expired");
    this.name = "TrackingNotFoundError";
  }
}

export class TrackingRequestError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`Tracking request failed with status ${status}`);
    this.name = "TrackingRequestError";
    this.status = status;
  }
}

export const trackingApi = {
  async get(token: string): Promise<PublicTrackingResponse> {
    const base = resolveTrackingApiBase();
    const key = trackingApiKey();
    const res = await fetch(
      `${base}/v1/delivery-mgmt/public/track/${encodeURIComponent(token)}`,
      {
        method: "GET",
        headers: key ? { "X-API-Key": key } : undefined,
      },
    );
    if (res.status === 404) {
      throw new TrackingNotFoundError();
    }
    if (!res.ok) {
      throw new TrackingRequestError(res.status);
    }
    return (await res.json()) as PublicTrackingResponse;
  },
};
